import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ImagePicker from "./ImagePicker";

/**
 * Branded rich-text editor wrapper around React-Quill.
 *
 * Why a wrapper?
 *   - We override the toolbar's image button to open our ImagePicker (so the
 *     admin can drop in a Site Image / Class Photo by reference, not just
 *     paste a giant data URL).
 *   - We force a sensible heading set + text colours that match the brand.
 *   - We expose a plain `(html) => void` API so callers don't need to know
 *     about Quill internals.
 *
 * Props:
 *   - value: string (HTML)
 *   - onChange: (html) => void
 *   - placeholder?: string
 *   - testid?: string
 *   - minHeight?: number (px)
 */
export default function RichTextEditor({ value, onChange, placeholder, testid = "rich-editor", minHeight = 320 }) {
  const quillRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const insertImage = (val) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    let src = val.src;
    // Gallery photo picks return both src (URL) and photo_id; the network URL
    // is the right thing to embed (won't bloat the email body with base64).
    if (val.image_base64 && !src) {
      src = `data:${val.mime_type};base64,${val.image_base64}`;
    }
    if (!src) return;
    editor.insertEmbed(range.index, "image", src, "user");
    editor.setSelection(range.index + 1);
    setPickerOpen(false);
  };

  // Toolbar config — heading sizes, lists, alignment, link, image (custom),
  // text colour, and clean. We intentionally exclude background-colour
  // (highlight) because it breaks the newsletter aesthetic in email clients —
  // admins were accidentally clicking it and the emails looked terrible.
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        [{ color: [] }],
        ["clean"],
      ],
      handlers: { image: () => setPickerOpen(true) },
    },
    clipboard: { matchVisual: false },
  }), []);

  const formats = [
    "header", "bold", "italic", "underline",
    "list", "align", "link", "image",
    "color",
  ];

  // Inject minimum-height + brand colours on first mount.
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (editor) editor.root.style.minHeight = `${minHeight}px`;
  }, [minHeight]);

  return (
    <div data-testid={testid} className="rich-editor">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      {pickerOpen && (
        <ImagePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={insertImage}
          title="Insert an image"
        />
      )}
      <style>{`
        .rich-editor .ql-toolbar.ql-snow {
          border-color: #E5E0D8;
          border-radius: 8px 8px 0 0;
          background: #FAF6EE;
        }
        .rich-editor .ql-container.ql-snow {
          border-color: #E5E0D8;
          border-radius: 0 0 8px 8px;
          font-family: 'Century Gothic','Jost','Avenir Next',sans-serif;
          font-size: 14px;
          background: #fff;
        }
        .rich-editor .ql-editor {
          color: #3A2A47;
        }
        .rich-editor .ql-editor h2 { color: #5D2C8F; font-family: Fraunces, Georgia, serif; }
        .rich-editor .ql-editor h3 { color: #5D2C8F; font-family: Fraunces, Georgia, serif; }
        .rich-editor .ql-snow .ql-stroke { stroke: #5D2C8F; }
        .rich-editor .ql-snow .ql-fill { fill: #5D2C8F; }
        .rich-editor .ql-snow .ql-picker-label { color: #5D2C8F; }
      `}</style>
    </div>
  );
}
